/**
 * Hisaab genius bar — voice / camera / similar / drift actions.
 * Calm-tech: collapsed by default, lazy-load chassis modules on first tap.
 */
import { store } from '../store.ts';
import type { Transaction, Summary } from '../types.ts';
import { formatINR, CATEGORY_DISPLAY } from '../types.ts';

const TPL = /* html */ `
<style>
:host {
  display: block;
  margin: 28px 0 12px;
  position: relative;
  z-index: 3;
}
.shell {
  border: 1px solid var(--rule-strong, oklch(28% 0.04 50));
  background: var(--paper, oklch(96% 0.020 80));
  padding: 16px 18px;
  border-radius: 2px;
  position: relative;
}
.shell::before {
  content: '';
  position: absolute;
  top: -1px; left: -1px; right: -1px; height: 3px;
  background: linear-gradient(90deg, var(--crimson, #9b1c1c) 0%, var(--crimson, #9b1c1c) 40%, transparent 100%);
}
.head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px dashed oklch(60% 0.040 50 / 0.4);
}
.eyebrow {
  font-family: var(--mono, monospace);
  font-size: 9px;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  color: var(--ink-dim, oklch(40% 0.04 50));
}
.head h3 {
  font-family: var(--display, serif);
  font-size: 22px;
  font-style: italic;
  font-weight: 600;
  margin: 0;
  color: var(--ink, oklch(20% 0.04 50));
}
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
}
.btn {
  appearance: none;
  cursor: pointer;
  font-family: var(--mono, monospace);
  font-size: 10px;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  background: oklch(99% 0.005 80);
  border: 1px solid var(--rule-strong, oklch(28% 0.04 50));
  padding: 14px 10px;
  border-radius: 2px;
  color: var(--ink, oklch(20% 0.04 50));
  transition: transform 100ms, background 120ms;
  display: grid;
  gap: 4px;
  text-align: left;
}
.btn:hover { background: var(--crimson-wash, oklch(94% 0.02 25 / 0.4)); }
.btn:active { transform: scale(0.97); }
.btn b {
  font-family: var(--display, serif);
  font-style: italic;
  font-size: 17px;
  letter-spacing: 0;
  text-transform: none;
  font-weight: 600;
}
.btn span {
  color: var(--ink-faint, oklch(50% 0.04 50));
  font-size: 9px;
}
.tray {
  margin-top: 16px;
  padding: 14px;
  background: oklch(94% 0.025 80 / 0.5);
  border-left: 3px solid var(--crimson, #9b1c1c);
  font-family: var(--display, serif);
  font-size: 14px;
  color: var(--ink, oklch(20% 0.04 50));
  display: none;
}
.tray.show { display: block; }
.tray h4 {
  font-family: var(--mono, monospace);
  font-size: 9px;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  margin: 0 0 8px;
  color: var(--ink-dim, oklch(40% 0.04 50));
}
.tray ul { margin: 0; padding-left: 1.2em; }
.tray li { margin: 4px 0; }
.tray .amt {
  color: var(--crimson, #9b1c1c);
  font-style: italic;
  font-weight: 700;
}
.tray .ok {
  font-family: var(--mono, monospace);
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}
.tray .input-row { display: flex; gap: 8px; margin-top: 10px; }
.tray .input-row input {
  flex: 1;
  font-family: var(--mono, monospace);
  padding: 10px;
  border: 1px solid var(--rule-strong, oklch(28% 0.04 50));
  background: oklch(99% 0.005 80);
  border-radius: 2px;
}
.tray .input-row button {
  font-family: var(--mono, monospace);
  font-size: 10px;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  padding: 10px 16px;
  background: var(--crimson, #9b1c1c);
  color: var(--paper, #f5ead1);
  border: none;
  cursor: pointer;
  border-radius: 2px;
}
</style>
<section class="shell">
  <div class="head">
    <div class="eyebrow">Tools · Voice · Camera · AI</div>
    <h3>The कलम is digital now</h3>
  </div>
  <div class="grid">
    <button class="btn" data-act="voice">
      <b>Voice entry</b>
      <span>"₹450 chai mausi ke saath"</span>
    </button>
    <button class="btn" data-act="receipt">
      <b>Scan receipt</b>
      <span>camera → structured txn</span>
    </button>
    <button class="btn" data-act="drift">
      <b>Drift watch</b>
      <span>weekly anomalies</span>
    </button>
    <button class="btn" data-act="similar">
      <b>Find similar</b>
      <span>embedding search</span>
    </button>
  </div>
  <div class="tray" id="tray"></div>
</section>
`;

export class HisaabGeniusBar extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #tray: HTMLDivElement | null = null;

  connectedCallback(): void {
    this.#shadow.innerHTML = TPL;
    this.#tray = this.#shadow.getElementById('tray') as HTMLDivElement;
    this.#shadow.querySelectorAll<HTMLButtonElement>('.btn').forEach((b) => {
      b.addEventListener('click', () => {
        const act = b.dataset.act;
        if (act === 'voice') void this.#voice();
        else if (act === 'receipt') void this.#receipt();
        else if (act === 'drift') void this.#drift();
        else if (act === 'similar') void this.#similar();
      });
    });
  }

  #renderTray(html: string): void {
    if (this.#tray === null) return;
    this.#tray.innerHTML = html;
    this.#tray.classList.add('show');
  }

  async #voice(): Promise<void> {
    this.#renderTray('<h4>Voice · listening</h4><div>Bolo: amount + jagah + saath kaun</div>');
    try {
      const { voiceToDraft, llmRefineVoice, draftToTransaction } = await import('../genius/voice-entry.ts');
      let draft = await voiceToDraft('en-IN');
      draft = await llmRefineVoice(draft);
      const cat = CATEGORY_DISPLAY[draft.category];
      const partyLine = typeof draft.party === 'string' ? ` · saath ${draft.party}` : '';
      this.#renderTray(`
        <h4>Voice · draft</h4>
        <div>"${escapeHtml(draft.transcript)}"</div>
        <ul>
          <li><span class="amt">${formatINR(draft.amount_inr)}</span> · ${cat}${partyLine}</li>
          <li>Confidence: ${(draft.confidence * 100).toFixed(0)}%</li>
        </ul>
        <div class="ok">✓ saved to draft buffer (review and commit from share)</div>
      `);
      stashDraft({ kind: 'voice', tx: draftToTransaction(draft) });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'voice unavailable';
      this.#renderTray(`<h4>Voice unavailable</h4><div>${escapeHtml(msg)}</div>`);
    }
  }

  async #receipt(): Promise<void> {
    this.#renderTray('<h4>Receipt scanner</h4><div>Pick a receipt photo from your phone:</div><input type="file" accept="image/*" id="rfile">');
    const inp = this.#shadow.querySelector('#rfile') as HTMLInputElement | null;
    if (inp === null) return;
    inp.addEventListener('change', async () => {
      const file = inp.files?.[0];
      if (file === undefined) return;
      this.#renderTray('<h4>Receipt · OCR running</h4><div>Tesseract WASM loading on first run...</div>');
      try {
        const { receiptFromFile, llmRefineDraft, draftToTransaction } = await import('../genius/receipt-ocr.ts');
        let draft = await receiptFromFile(file);
        draft = await llmRefineDraft(draft);
        const cat = CATEGORY_DISPLAY[draft.category];
        this.#renderTray(`
          <h4>Receipt · draft</h4>
          <ul>
            <li><span class="amt">${formatINR(draft.amount_inr)}</span> at ${escapeHtml(draft.merchant_display)}</li>
            <li>Category · ${cat}</li>
            <li>Date · ${draft.date}</li>
            <li>Items · ${draft.items.length}</li>
            <li>Confidence · ${(draft.confidence * 100).toFixed(0)}%</li>
          </ul>
          <div class="ok">✓ saved to draft buffer</div>
        `);
        stashDraft({ kind: 'receipt', tx: draftToTransaction(draft) });
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'OCR failed';
        this.#renderTray(`<h4>OCR failed</h4><div>${escapeHtml(msg)}</div>`);
      }
    });
  }

  async #drift(): Promise<void> {
    this.#renderTray('<h4>Drift · computing</h4>');
    const summary = store.state.summary;
    if (summary === null) {
      this.#renderTray('<h4>Drift</h4><div>Open the book first.</div>');
      return;
    }
    try {
      const { weeklyDrift } = await import('../genius/anomaly-watch.ts');
      const nudges = weeklyDrift(summary);
      if (nudges.length === 0) {
        this.#renderTray('<h4>Drift · this week</h4><div>Sab tight. No 50%+ swings vs 3-week baseline.</div>');
        return;
      }
      const lines = nudges
        .slice(0, 5)
        .map((n) => {
          const arrow = n.verdict === 'rising' ? '↑' : '↓';
          return `<li>${arrow} ${escapeHtml(n.humanCategory)} · <span class="amt">${formatINR(n.thisWeek)}</span> vs ${formatINR(n.baseline)} baseline · ${n.pct.toFixed(0)}%</li>`;
        })
        .join('');
      this.#renderTray(`<h4>Drift · this week</h4><ul>${lines}</ul>`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'drift failed';
      this.#renderTray(`<h4>Drift failed</h4><div>${escapeHtml(msg)}</div>`);
    }
  }

  async #similar(): Promise<void> {
    this.#renderTray('<h4>Similar · pick a seed</h4><div>Type merchant or category:</div><div class="input-row"><input id="sq" placeholder="e.g. Swiggy"><button id="sgo">Find</button></div>');
    const inp = this.#shadow.querySelector('#sq') as HTMLInputElement | null;
    const btn = this.#shadow.querySelector('#sgo') as HTMLButtonElement | null;
    if (inp === null || btn === null) return;
    btn.addEventListener('click', async () => {
      const q = inp.value.trim();
      if (q.length === 0) return;
      const summary = store.state.summary;
      if (summary === null) return;
      this.#renderTray('<h4>Similar · indexing on-device</h4><div>First run downloads ~25MB embedder. Subsequent calls instant.</div>');
      try {
        const { indexTransactions, findSimilar } = await import('../genius/similar-tx.ts');
        await indexTransactions(summary.transactions);
        const seed = summary.transactions.find((t) => t.merchant_display.toLowerCase().includes(q.toLowerCase())) ?? buildSeedFromQuery(q, summary);
        const results = await findSimilar(seed, 5);
        if (results.length === 0) {
          this.#renderTray('<h4>Similar</h4><div>No close matches.</div>');
          return;
        }
        const lines = results
          .map(
            (r) =>
              `<li>${escapeHtml(r.record.merchant_display)} · <span class="amt">${formatINR(r.record.amount_inr)}</span> · ${r.record.date} · cos ${r.score.toFixed(2)}</li>`,
          )
          .join('');
        this.#renderTray(`<h4>Similar to "${escapeHtml(q)}"</h4><ul>${lines}</ul>`);
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'similar failed';
        this.#renderTray(`<h4>Similar failed</h4><div>${escapeHtml(msg)}</div>`);
      }
    });
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);
}

function buildSeedFromQuery(q: string, summary: Summary): Transaction {
  const first = summary.transactions[0];
  const cat = first?.category ?? 'other';
  return {
    id: 'seed',
    source: 'manual',
    source_ref: 'query',
    merchant_id: q.toLowerCase().replace(/[^a-z0-9]+/g, '_'),
    merchant_display: q,
    amount_inr: 0,
    items: [q],
    category: cat,
    date: new Date().toISOString().slice(0, 10),
    confidence: 0.5,
    detected_at: new Date().toISOString(),
  };
}

type DraftBuf = { kind: 'voice' | 'receipt'; tx: Transaction };

function stashDraft(buf: DraftBuf): void {
  const raw = localStorage.getItem('hisaab:drafts');
  const list: DraftBuf[] = raw === null ? [] : (JSON.parse(raw) as DraftBuf[]);
  list.push(buf);
  localStorage.setItem('hisaab:drafts', JSON.stringify(list.slice(-50)));
}

customElements.define('hisaab-genius-bar', HisaabGeniusBar);
